import { useTranslation } from 'react-i18next';

import { formatSpokenMonth } from '../utils/formatMonth';

interface TrendDataTableProps {
  caption: string;
  series: string[];
  points: ReadonlyArray<{ month: string } & Record<string, unknown>>;
  formatValue: (value: number) => string;
}

/**
 * The values behind a line chart, one row per month and one column per series, for screen
 * readers. The chart itself is announced only by its summary, so this is where its data lives.
 */
export const TrendDataTable = ({ caption, series, points, formatValue }: TrendDataTableProps) => {
  const { t, i18n } = useTranslation('analysis');

  if (points.length === 0) return null;

  return (
    <table className="sr-only">
      <caption>{caption}</caption>
      <thead>
        <tr>
          <th scope="col">{t('analysisResult.trendChart.month')}</th>
          {series.map((name) => (
            <th key={name} scope="col">
              {name}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {points.map((point) => (
          <tr key={point.month}>
            <th scope="row">{formatSpokenMonth(point.month, i18n.language)}</th>
            {series.map((name) => {
              const value = point[name];
              return (
                <td key={name}>
                  {typeof value === 'number'
                    ? formatValue(value)
                    : t('analysisResult.trendChart.noValue')}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
};
